/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import type { HTMLAttributes, ReactNode } from 'react';

import Color from 'color';
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PrimativeFieldProps, PrimativeFormPropsOmit } from '../../lib/utils';

import { debounce } from '../../lib/utils';
import { Slider } from './slider';

// Helper types
type Vector3 = [number, number, number];
type ColorMode = 'hsl' | 'oklch' | 'rgb' | 'hex';
//  type ColorValue = `hsl(${number}, ${number}%, ${number}%)` | `oklch(${number}% ${number} ${number})` | `rgb(${number}, ${number}, ${number})` | `#${string}`;

// Context for sharing HSL state
type ColorPickerContextValue = {
    hue: number;
    saturation: number;
    lightness: number;
    color: ReturnType<typeof Color.hsl>;
    disabled?: boolean;
    setHue: (hue: number) => void;
    setSaturation: (saturation: number) => void;
    setLightness: (lightness: number) => void;
    updateFromColor: (newColor: ReturnType<typeof Color>) => void;
};

const ColorPickerContext = createContext<ColorPickerContextValue | null>(null);

function useColorPickerContext() {
    const context = use(ColorPickerContext);
    if (!context) {
        throw new Error('ColorPicker subcomponents must be used within ColorPicker');
    }
    return context;
}

// Helper function to parse color string to HSL
function parseColorToHSL(colorValue: string | null | undefined, defaultColor: string): [number, number, number] {
    if (!colorValue) {
        colorValue = defaultColor;
    }

    try {
        const color = Color(colorValue);
        const [h, s, l] = color.hsl().array() as Vector3;
        return [Math.round(h), Math.round(s), Math.round(l)];
    }
    catch {
        // Fallback to default color if parsing fails
        try {
            const color = Color(defaultColor);
            const [h, s, l] = color.hsl().array() as Vector3;
            return [Math.round(h), Math.round(s), Math.round(l)];
        }
        catch {
            return [0, 0, 0];
        }
    }
}

// Helper function to convert HSL to format based on mode
function convertHSLToFormat(hue: number, saturation: number, lightness: number, mode: ColorMode): string {
    const color = Color.hsl(hue, saturation, lightness);
    switch (mode) {
        case 'hsl':
            return color.hsl().string();
        case 'rgb':
            return color.rgb().string();
        case 'oklch': {
            // Note: The 'color' library doesn't natively support OKLCH conversion.
            // For now, we return HSL format as a fallback.
            // To properly support OKLCH, you would need a library like 'culori' or implement the conversion manually.
            // This is a known limitation and OKLCH mode will output HSL format.
            return color.hsl().string();
        }
        case 'hex':
        default:
            return color.hex();
    }
}

export type ColorPickerProps = Omit<HTMLAttributes<HTMLDivElement>, PrimativeFormPropsOmit> & PrimativeFieldProps<string | null> & {
    /**
     * Default color value (can be in any supported format)
     */
    defaultColor?: string;
    /**
     * Output format mode
     * @default 'hex'
     */
    mode?: ColorMode;
    /**
     * Children components to compose
     */
    children?: ReactNode;
};

export function ColorPickerRoot(props: ColorPickerProps) {
    const {
        name,
        value,
        onChange,
        onBlur,
        disabled,
        defaultColor = '#000000',
        mode = 'hex',
        children,
        className,
        id,
        ...restProps
    } = props;

    const [hue, setHue] = useState<number>(180);
    const [saturation, setSaturation] = useState<number>(50);
    const [lightness, setLightness] = useState<number>(50);

    // Parse initial value to HSL
    useEffect(() => {
        const [h, s, l] = parseColorToHSL(value ?? null, defaultColor);
        setHue(h);
        setSaturation(s);
        setLightness(l);
    }, [value, defaultColor]);

    // Create Color instance
    const color = Color.hsl(hue, saturation, lightness);

    // Convert to output format and debounce onChange
    useEffect(() => {
        const debouncedOnChange = debounce((colorString: string) => {
            if (colorString === '') {
                onChange(null);
            }
            else {
                onChange(colorString);
            }
        }, 500);

        const colorString = convertHSLToFormat(hue, saturation, lightness, mode);
        debouncedOnChange(colorString);

        return () => debouncedOnChange.cancel();
    }, [hue, saturation, lightness, mode, onChange]);

    // Helper function to update color from Color instance
    const updateFromColor = useCallback((newColor: ReturnType<typeof Color>) => {
        const [h, s, l] = newColor.hsl().array() as Vector3;
        setHue(Math.round(h));
        setSaturation(Math.round(s));
        setLightness(Math.round(l));
    }, []);

    const contextValue = useMemo<ColorPickerContextValue>(() => ({
        hue,
        saturation,
        lightness,
        color,
        disabled,
        setHue,
        setSaturation,
        setLightness,
        updateFromColor,
    }), [hue, saturation, lightness, color, disabled, setHue, setSaturation, setLightness, updateFromColor]);

    return (
        <ColorPickerContext value={contextValue}>
            <div
                id={id ?? name}
                className={className}
                onBlur={onBlur}
                {...restProps}
            >
                {children}
            </div>
        </ColorPickerContext>
    );
}

ColorPickerRoot.displayName = 'ColorPicker';

// Picker subcomponent - 2D color picker area
export type ColorPickerPickerProps = Omit<HTMLAttributes<HTMLDivElement>, 'onMouseDown'> & {
    className?: string;
};

export function ColorPickerPicker(props: ColorPickerPickerProps) {
    const { className, ...restProps } = props;
    const { hue, color, disabled, setSaturation, setLightness } = useColorPickerContext();
    const pickerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    // 2D Picker interaction handlers
    const handlePickerInteraction = useCallback((e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
        if (!pickerRef.current || disabled)
            return;

        const rect = pickerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

        // Calculate HSV values from position
        const saturationHSV = (x / rect.width) * 100;
        const valueHSV = 100 - (y / rect.height) * 100;

        const newColor = Color.hsv(hue, saturationHSV, valueHSV);

        // Convert HSV to HSL
        const [, newSaturation, newLightness] = newColor.hsl().array() as Vector3;

        setSaturation(Math.round(newSaturation));
        setLightness(Math.round(newLightness));
    }, [hue, disabled, setSaturation, setLightness]);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled)
            return;
        setIsDragging(true);
        handlePickerInteraction(e);
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (isDragging) {
            handlePickerInteraction(e);
        }
    }, [isDragging, handlePickerInteraction]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add and remove global mouse event listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Calculate HSV position for handle
    const [, saturationHSV, valueHSV] = color.hsv().array() as Vector3;
    const handleX = saturationHSV;
    const handleY = 100 - valueHSV;

    return (
        <div
            ref={pickerRef}
            onMouseDown={handleMouseDown}
            className={className ?? 'relative w-full h-64 rounded-lg border-2 border-gray-300 shadow-lg cursor-crosshair overflow-hidden'}
            style={{
                background: `
                    linear-gradient(to top, black, transparent),
                    linear-gradient(to right, white, hsl(${hue}, 100%, 50%))
                `,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'crosshair',
            }}
            {...restProps}
        >
            {/* Color Picker Handle */}
            <div
                className="absolute w-5 h-5 border-3 border-white rounded-full shadow-lg pointer-events-none"
                style={{
                    left: `${handleX}%`,
                    top: `${handleY}%`,
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 0 0 1.5px white, 0 0 0 2.5px black, 0 2px 4px rgba(0,0,0,0.3)',
                }}
            />
        </div>
    );
}

ColorPickerPicker.displayName = 'ColorPicker.Picker';

// Preview subcomponent
export type ColorPickerPreviewProps = HTMLAttributes<HTMLDivElement> & {
    showLabel?: boolean;
    className?: string;
    previewClassName?: string;
};

export function ColorPickerPreview(props: ColorPickerPreviewProps) {
    const { showLabel = true, className, previewClassName, ...restProps } = props;
    const { hue, saturation, lightness, color } = useColorPickerContext();
    const colorString = color.hsl().string();

    return (
        <div className={className ?? 'flex items-center gap-4'} {...restProps}>
            <div
                className={previewClassName ?? 'w-20 h-12 rounded-lg border-2 border-gray-300 shadow-sm flex-shrink-0'}
                style={{ backgroundColor: colorString }}
            />
            {showLabel && (
                <div className="flex-1">
                    <p className="text-lg font-mono font-semibold">{colorString}</p>
                    <p className="text-sm text-gray-600">
                        H:
                        {' '}
                        {hue}
                        ° | S:
                        {' '}
                        {saturation}
                        % | L:
                        {' '}
                        {lightness}
                        %
                    </p>
                </div>
            )}
        </div>
    );
}

ColorPickerPreview.displayName = 'ColorPicker.Preview';

// Formats subcomponent
export type ColorPickerFormatsProps = HTMLAttributes<HTMLDivElement> & {
    className?: string;
    showHex?: boolean;
    showRgb?: boolean;
    showHsl?: boolean;
};

export function ColorPickerFormats(props: ColorPickerFormatsProps) {
    const { className, showHex = true, showRgb = true, showHsl = true, ...restProps } = props;
    const { color } = useColorPickerContext();

    const hex = color.hex();
    const rgb = color.rgb();
    const rgbString = rgb.string();
    const rgbArray = rgb.array();
    const hslString = color.hsl().string();

    // Copy to clipboard function
    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        }
        catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const formats = [];
    if (showHex) {
        formats.push(
            <button
                key="hex"
                type="button"
                onClick={() => copyToClipboard(hex)}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors text-left cursor-pointer"
                title="Click to copy"
            >
                <p className="text-xs text-gray-500 mb-1">HEX</p>
                <p className="font-mono font-semibold">{hex}</p>
            </button>,
        );
    }
    if (showRgb) {
        formats.push(
            <button
                key="rgb"
                type="button"
                onClick={() => copyToClipboard(rgbString)}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors text-left cursor-pointer"
                title="Click to copy"
            >
                <p className="text-xs text-gray-500 mb-1">RGB</p>
                <p className="font-mono font-semibold text-sm">{rgbString}</p>
                <p className="text-xs text-gray-400 mt-1">
                    R:
                    {' '}
                    {Math.round(rgbArray[0])}
                    {' '}
                    G:
                    {' '}
                    {Math.round(rgbArray[1])}
                    {' '}
                    B:
                    {' '}
                    {Math.round(rgbArray[2])}
                </p>
            </button>,
        );
    }
    if (showHsl) {
        formats.push(
            <button
                key="hsl"
                type="button"
                onClick={() => copyToClipboard(hslString)}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors text-left cursor-pointer"
                title="Click to copy"
            >
                <p className="text-xs text-gray-500 mb-1">HSL</p>
                <p className="font-mono font-semibold text-sm">{hslString}</p>
            </button>,
        );
    }

    return (
        <div className={className ?? 'mb-8 grid grid-cols-1 md:grid-cols-3 gap-4'} {...restProps}>
            {formats}
        </div>
    );
}

ColorPickerFormats.displayName = 'ColorPicker.Formats';

// Hue Slider subcomponent
export type ColorPickerHueSliderProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
    className?: string;
    showLabel?: boolean;
};

export function ColorPickerHueSlider(props: ColorPickerHueSliderProps) {
    const { className, showLabel = true, ...restProps } = props;
    const { hue, disabled, setHue } = useColorPickerContext();

    return (
        <div className={className ?? 'space-y-2'} {...restProps}>
            {showLabel && (
                <label className="text-sm font-medium flex items-center justify-between">
                    <span>Hue</span>
                    <span className="text-gray-600">
                        {hue}
                        °
                    </span>
                </label>
            )}
            <Slider
                name="hue"
                value={[hue]}
                onChange={v => setHue(v[0])}
                min={0}
                max={360}
                disabled={disabled}
                trackStyle={{
                    background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
                }}
                trackClassName="h-3"
                showActiveSegment={false}
            />
        </div>
    );
}

ColorPickerHueSlider.displayName = 'ColorPicker.HueSlider';

// Saturation Slider subcomponent
export type ColorPickerSaturationSliderProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
    className?: string;
    showLabel?: boolean;
};

export function ColorPickerSaturationSlider(props: ColorPickerSaturationSliderProps) {
    const { className, showLabel = true, ...restProps } = props;
    const { hue, saturation, lightness, disabled, setSaturation } = useColorPickerContext();

    return (
        <div className={className ?? 'space-y-2'} {...restProps}>
            {showLabel && (
                <label className="text-sm font-medium flex items-center justify-between">
                    <span>Saturation</span>
                    <span className="text-gray-600">
                        {saturation}
                        %
                    </span>
                </label>
            )}
            <Slider
                name="saturation"
                value={[saturation]}
                onChange={v => setSaturation(v[0])}
                min={0}
                max={100}
                disabled={disabled}
                trackStyle={{
                    background: `linear-gradient(to right, hsl(${hue}, 0%, ${lightness}%), hsl(${hue}, 100%, ${lightness}%))`,
                }}
                trackClassName="h-3"
                showActiveSegment={false}
            />
        </div>
    );
}

ColorPickerSaturationSlider.displayName = 'ColorPicker.SaturationSlider';

// Lightness Slider subcomponent
export type ColorPickerLightnessSliderProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
    className?: string;
    showLabel?: boolean;
};

export function ColorPickerLightnessSlider(props: ColorPickerLightnessSliderProps) {
    const { className, showLabel = true, ...restProps } = props;
    const { hue, saturation, lightness, disabled, setLightness } = useColorPickerContext();

    return (
        <div className={className ?? 'space-y-2'} {...restProps}>
            {showLabel && (
                <label className="text-sm font-medium flex items-center justify-between">
                    <span>Lightness</span>
                    <span className="text-gray-600">
                        {lightness}
                        %
                    </span>
                </label>
            )}
            <Slider
                name="lightness"
                value={[lightness]}
                onChange={v => setLightness(v[0])}
                min={0}
                max={100}
                disabled={disabled}
                trackStyle={{
                    background: `linear-gradient(to right, hsl(${hue}, ${saturation}%, 0%), hsl(${hue}, ${saturation}%, 50%), hsl(${hue}, ${saturation}%, 100%))`,
                }}
                trackClassName="h-3"
                showActiveSegment={false}
            />
        </div>
    );
}

ColorPickerLightnessSlider.displayName = 'ColorPicker.LightnessSlider';

// Manipulations subcomponent
export type ColorPickerManipulationsProps = HTMLAttributes<HTMLDivElement> & {
    className?: string;
    showTitle?: boolean;
    buttonClassName?: string;
};

export function ColorPickerManipulations(props: ColorPickerManipulationsProps) {
    const { className, showTitle = true, buttonClassName, ...restProps } = props;
    const { color, disabled, updateFromColor } = useColorPickerContext();

    const manipulations = [
        { label: 'Lighten', action: () => updateFromColor(color.lighten(0.1)) },
        { label: 'Darken', action: () => updateFromColor(color.darken(0.1)) },
        { label: 'Saturate', action: () => updateFromColor(color.saturate(0.1)) },
        { label: 'Desaturate', action: () => updateFromColor(color.desaturate(0.1)) },
        { label: 'Rotate +30°', action: () => updateFromColor(color.rotate(30)) },
        { label: 'Rotate -30°', action: () => updateFromColor(color.rotate(-30)) },
        { label: 'Invert', action: () => updateFromColor(color.negate()) },
        { label: 'Grayscale', action: () => updateFromColor(color.grayscale()) },
    ];

    return (
        <div className={className ?? 'mb-8'} {...restProps}>
            {showTitle && <h2 className="text-lg font-semibold mb-3">Color Manipulations</h2>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {manipulations.map(({ label, action }) => (
                    <button
                        key={label}
                        type="button"
                        onClick={action}
                        disabled={disabled}
                        className={buttonClassName ?? 'px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed'}
                    >
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
}

ColorPickerManipulations.displayName = 'ColorPicker.Manipulations';

// Harmonies subcomponent
export type ColorPickerHarmoniesProps = HTMLAttributes<HTMLDivElement> & {
    className?: string;
    showTitle?: boolean;
    harmonyClassName?: string;
};

export function ColorPickerHarmonies(props: ColorPickerHarmoniesProps) {
    const { className, showTitle = true, harmonyClassName, ...restProps } = props;
    const { color, disabled, updateFromColor } = useColorPickerContext();

    const harmonies = [
        { label: 'Complementary', color: color.rotate(180) },
        { label: 'Triadic 1', color: color.rotate(120) },
        { label: 'Triadic 2', color: color.rotate(240) },
        { label: 'Analogous 1', color: color.rotate(30) },
        { label: 'Analogous 2', color: color.rotate(-30) },
    ];

    return (
        <div className={className ?? 'mb-8'} {...restProps}>
            {showTitle && <h2 className="text-lg font-semibold mb-3">Color Harmonies</h2>}
            <div className="flex gap-3 overflow-x-auto pb-2">
                {harmonies.map(({ label, color: harmonyColor }) => (
                    <button
                        key={label}
                        type="button"
                        onClick={() => updateFromColor(harmonyColor)}
                        disabled={disabled}
                        className="flex-shrink-0 group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <div
                            className={harmonyClassName ?? 'w-20 h-20 rounded-lg border-2 border-gray-300 group-hover:border-gray-400 transition-colors shadow-sm'}
                            style={{ backgroundColor: harmonyColor.hsl().string() }}
                        />
                        <p className="text-xs text-center mt-1 text-gray-600">{label}</p>
                    </button>
                ))}
            </div>
        </div>
    );
}

ColorPickerHarmonies.displayName = 'ColorPicker.Harmonies';

export const ColorPicker = Object.assign(ColorPickerRoot, {
    Picker: ColorPickerPicker,
    Preview: ColorPickerPreview,
    Formats: ColorPickerFormats,
    HueSlider: ColorPickerHueSlider,
    SaturationSlider: ColorPickerSaturationSlider,
    LightnessSlider: ColorPickerLightnessSlider,
    Manipulations: ColorPickerManipulations,
    Harmonies: ColorPickerHarmonies,
});
